/**
 * Luxire Product Recommendations v4
 *
 * Strategy:
 *   "More Like This"     → products from the SAME collection
 *   "Complete the Look"  → products from a DIFFERENT (complementary) collection
 *
 * Collection mapping:
 *   Pants collections  ↔  Shirt collections
 *   (dress-pants, chinos, jeans, gurkha-pants) ↔ (business-shirts, casual-shirts, polo)
 *
 * Lazy-loaded via IntersectionObserver when the section enters viewport.
 */

(function () {
  'use strict';

  /* ── Collection Classification ── */
  var PANTS_COLLECTIONS = ['dress-pants', 'chinos', 'jeans', 'gurkha-pants'];
  var SHIRT_COLLECTIONS = ['business-shirts', 'casual-shirts', 'polo'];

  /* Meta/aggregate collections to skip when finding the "primary" collection */
  var SKIP_COLLECTIONS = ['all-pants', 'all-shirts', 'best-sellers', 'frontpage', 'gst', 'luxire-hidden-internal'];

  /* Complementary collection to pull "Complete the Look" from */
  var COMPLEMENT_MAP = {
    /* pants → shirts */
    'dress-pants':     'all-shirts',
    'chinos':          'casual-shirts',
    'jeans':           'casual-shirts',
    'gurkha-pants':    'casual-shirts',
    'all-pants':       'all-shirts',
    /* shirts → pants */
    'business-shirts': 'dress-pants',
    'casual-shirts':   'all-pants',
    'polo':            'chinos',
    'all-shirts':      'all-pants'
  };


  /* ── Money Formatter ──
     Shopify's /collections/.../products.json returns prices as strings
     in the store's currency unit (e.g. "4700.00" for Rs. 4,700).
     No division needed — pass straight through to formatter. */
  function formatMoney(priceStr) {
    var amount = parseFloat(priceStr) || 0;
    if (window.Luxire && Luxire.Pricing && Luxire.Pricing._formatMoney) {
      return Luxire.Pricing._formatMoney(amount);
    }
    if (window.LuxireFormatMoney) {
      return LuxireFormatMoney(amount);
    }
    /* Fallback: simple INR format */
    var str = amount.toFixed(2);
    var parts = str.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return 'Rs. ' + parts.join('.');
  }


  /* ── Render Product Card ── */
  function renderCard(product) {
    var img = '';
    var rawSrc = product.featured_image || (product.images && product.images[0]) || '';
    if (typeof rawSrc === 'object' && (rawSrc.url || rawSrc.src)) rawSrc = rawSrc.url || rawSrc.src;
    if (typeof rawSrc === 'string' && rawSrc) {
      img = rawSrc + (rawSrc.indexOf('?') === -1 ? '?width=300' : '&width=300');
    }

    var url = '/products/' + product.handle;
    var price = '';
    if (product.variants && product.variants.length) {
      price = formatMoney(product.variants[0].price);
    } else if (product.price) {
      price = formatMoney(product.price);
    }
    var name = product.title || '';

    return '<a href="' + url + '" class="rec-card" data-handle="' + (product.handle || '') + '">' +
      '<div class="rec-card__img-wrap">' +
        (img ? '<img src="' + img + '" alt="' + name.replace(/"/g, '&quot;') + '" loading="lazy" width="300">' : '') +
      '</div>' +
      '<div class="rec-card__info">' +
        '<span class="rec-card__name">' + name + '</span>' +
        '<span class="rec-card__price">' + price + '</span>' +
      '</div></a>';
  }


  /* ── Build Scroll Wrap with Arrows ── */
  function buildScrollWrap(items) {
    var cards = '';
    for (var i = 0; i < items.length; i++) cards += renderCard(items[i]);
    return '<div class="rec-section__scroll-wrap" data-rec-scroll-wrap>' +
      '<div class="rec-section__fade rec-section__fade--left" data-rec-fade="left"></div>' +
      '<div class="rec-section__fade rec-section__fade--right is-visible" data-rec-fade="right"></div>' +
      '<button class="rec-section__arrow rec-section__arrow--left" data-rec-arrow="left" type="button" aria-label="Scroll left">' +
        '<svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="11 4 6 9 11 14"/></svg>' +
      '</button>' +
      '<button class="rec-section__arrow rec-section__arrow--right" data-rec-arrow="right" type="button" aria-label="Scroll right">' +
        '<svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="7 4 12 9 7 14"/></svg>' +
      '</button>' +
      '<div class="rec-section__scroll" data-rec-scroll>' + cards + '</div>' +
    '</div>';
  }


  /* ── Render Sections ── */
  function renderSections(container, similar, complement) {
    if (!container) return;
    var html = '';

    if (similar && similar.length) {
      html += '<div class="rec-section" data-rec="similar">' +
        '<h3 class="rec-section__title">More Like This</h3>' +
        buildScrollWrap(similar) +
        '</div>';
    }

    if (complement && complement.length) {
      html += '<div class="rec-section" data-rec="complement">' +
        '<h3 class="rec-section__title">Complete the Look</h3>' +
        buildScrollWrap(complement) +
        '</div>';
    }

    if (!html) {
      html = '<p class="rec-section__empty">Explore our full collection for more options.</p>';
    }

    container.innerHTML = html;
    initScrollArrows(container);
  }


  /* ── Scroll Arrows ── */
  function initScrollArrows(container) {
    container.querySelectorAll('[data-rec-scroll-wrap]').forEach(function (wrap) {
      var scroll = wrap.querySelector('[data-rec-scroll]');
      var arrowL = wrap.querySelector('[data-rec-arrow="left"]');
      var arrowR = wrap.querySelector('[data-rec-arrow="right"]');
      var fadeL = wrap.querySelector('[data-rec-fade="left"]');
      var fadeR = wrap.querySelector('[data-rec-fade="right"]');
      if (!scroll) return;

      function update() {
        var sl = scroll.scrollLeft;
        var maxScroll = scroll.scrollWidth - scroll.clientWidth;
        var atLeft = sl < 5;
        var atRight = sl >= maxScroll - 5;
        if (arrowL) arrowL.classList.toggle('is-visible', !atLeft);
        if (arrowR) arrowR.classList.toggle('is-visible', !atRight);
        if (fadeL) fadeL.classList.toggle('is-visible', !atLeft);
        if (fadeR) fadeR.classList.toggle('is-visible', !atRight);
      }

      scroll.addEventListener('scroll', update, { passive: true });
      if (arrowL) arrowL.addEventListener('click', function () {
        scroll.scrollBy({ left: -200, behavior: 'smooth' });
      });
      if (arrowR) arrowR.addEventListener('click', function () {
        scroll.scrollBy({ left: 200, behavior: 'smooth' });
      });
      setTimeout(update, 100);
    });
  }


  /* ── Fetch products from a collection ── */
  function fetchCollection(handle, limit) {
    var url = '/collections/' + handle + '/products.json?limit=' + (limit || 10);
    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('Collection fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        return data.products || [];
      });
  }


  /* ── Shuffle array (Fisher-Yates) for variety ── */
  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var temp = arr[i];
      arr[i] = arr[j];
      arr[j] = temp;
    }
    return arr;
  }


  /* ── Find the primary (most specific) collection ── */
  function findPrimaryCollection(handles) {
    for (var i = 0; i < handles.length; i++) {
      if (SKIP_COLLECTIONS.indexOf(handles[i]) === -1) return handles[i];
    }
    return handles[0] || '';
  }


  /* ── Initialize (Lazy — IntersectionObserver) ── */
  function init() {
    var container = document.querySelector('[data-product-recommendations]');
    if (!container) return;

    var productId = container.dataset.productId;
    if (!productId) return;

    var allCollections = (container.dataset.productCollections || '').split(',').filter(Boolean);
    var primaryCollection = findPrimaryCollection(allCollections);
    var complementCollection = COMPLEMENT_MAP[primaryCollection] || '';

    /* If no complement mapping found, try to infer from all collections */
    if (!complementCollection) {
      var isPants = allCollections.some(function (h) { return PANTS_COLLECTIONS.indexOf(h) !== -1 || h === 'all-pants'; });
      var isShirt = allCollections.some(function (h) { return SHIRT_COLLECTIONS.indexOf(h) !== -1 || h === 'all-shirts'; });
      if (isPants) complementCollection = 'all-shirts';
      else if (isShirt) complementCollection = 'all-pants';
    }

    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          observer.disconnect();
          loadRecommendations(container, productId, primaryCollection, complementCollection);
          break;
        }
      }
    }, { rootMargin: '300px' });

    observer.observe(container);
    initScrollArrows(container);
  }


  /* ── Load both sections ── */
  function loadRecommendations(container, productId, primaryColl, complementColl) {
    var similarPromise = primaryColl
      ? fetchCollection(primaryColl, 20)
      : Promise.resolve([]);

    var complementPromise = complementColl
      ? fetchCollection(complementColl, 20)
      : Promise.resolve([]);

    Promise.all([similarPromise, complementPromise])
      .then(function (results) {
        var sameCollProducts = results[0];
        var diffCollProducts = results[1];

        /* Filter out the current product and shuffle for variety */
        var similar = shuffle(
          sameCollProducts.filter(function (p) { return String(p.id) !== String(productId); })
        ).slice(0, 10);

        var complement = shuffle(
          diffCollProducts.filter(function (p) { return String(p.id) !== String(productId); })
        ).slice(0, 10);

        renderSections(container, similar, complement);
      })
      .catch(function (err) {
        console.warn('Luxire recommendations:', err);
        container.innerHTML = '';
      });
  }


  /* ── Public API ── */
  window.LuxireRecommendations = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
